import { useCallback, useRef } from 'react';
import type { MouseEvent, PointerEvent } from 'react';

/**
 * Fermeture d'un fond (overlay) au clic, sans fausse fermeture quand le geste
 * a commencé à l'intérieur du dialogue.
 *
 * Le DOM dispatche `click` sur l'**ancêtre commun le plus proche** des cibles de
 * `pointerdown` et `pointerup`. Un appui dans le dialogue relâché sur le fond
 * produit donc un `click` dont la cible EST le fond : le seul test
 * `e.target === e.currentTarget` ferme alors le dialogue à tort (sélection de
 * texte débordante, glisser d'un curseur, redimensionnement).
 *
 * L'origine du geste est mémorisée en phase de **capture** — un descendant qui
 * appelle `stopPropagation` ne peut donc pas la masquer — et la fermeture n'a
 * lieu que si le geste a commencé *et* fini sur le fond lui-même.
 *
 * @example
 * const overlayProps = useOverlayDismiss(onClose, closeOnOverlay);
 * return <div className={styles.overlay} {...overlayProps}>…</div>;
 */
export function useOverlayDismiss(onDismiss: () => void, enabled = true) {
  const originRef = useRef<EventTarget | null>(null);

  const onPointerDownCapture = useCallback((e: PointerEvent<HTMLElement>) => {
    originRef.current = e.target;
  }, []);

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const startedOnOverlay = originRef.current === e.currentTarget;
      originRef.current = null;
      if (enabled && startedOnOverlay && e.target === e.currentTarget) {
        onDismiss();
      }
    },
    [enabled, onDismiss],
  );

  return { onPointerDownCapture, onClick };
}

export default useOverlayDismiss;
